import React, { useState, useCallback } from 'react';
import {
  Box,
  Input,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  Option,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert
} from '@mui/joy';
import {
  Calendar,
  TextT as Type,
  CheckSquare,
  CircleDashed as CircleDot,
  CaretDown as ChevronDown,
  PenNib as PenTool,
  Check
} from '@phosphor-icons/react';
import type { ContractField } from '../../../types';

interface SigningFieldRendererProps {
  fields: ContractField[];
  fieldValues: Record<string, any>;
  currentPage?: number;
  scale: number;
  onFieldValueChange?: (fieldId: string, value: any) => void;
  onFieldChange?: (fieldId: number, value: string) => void;
  recipientRole?: string;
  touchOptimized?: boolean;
  onSignatureFieldClick?: (fieldId: string) => void;
}

interface InteractiveFieldProps {
  field: ContractField;
  value: any;
  scale: number;
  onChange: (value: any) => void;
  onClose: () => void;
  touchOptimized?: boolean;
}

const InteractiveField: React.FC<InteractiveFieldProps> = ({
  field,
  value,
  scale,
  onChange,
  onClose,
  touchOptimized = false
}) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [showDatePicker, setShowDatePicker] = useState(false);

  const handleSave = useCallback(() => {
    onChange(localValue);
    onClose();
  }, [localValue, onChange, onClose]);

  const handleCancel = useCallback(() => {
    setLocalValue(value || '');
    onClose();
  }, [value, onClose]);

  const renderFieldInput = () => {
    switch (field.type) {
      case 'text':
        return (
          <Input
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            placeholder={field.label || 'Enter text'}
            size={touchOptimized ? "lg" : "sm"}
            autoFocus
            sx={touchOptimized ? { fontSize: '18px', minHeight: '56px' } : {}}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            size={touchOptimized ? "lg" : "sm"}
            autoFocus
            sx={touchOptimized ? { fontSize: '18px', minHeight: '56px' } : {}}
          />
        );

      case 'checkbox':
        return (
          <Checkbox
            checked={localValue === true}
            onChange={(e) => setLocalValue(e.target.checked)}
            label={field.label || 'Check this box'}
            size={touchOptimized ? "lg" : "md"}
            sx={touchOptimized ? {
              '& .MuiCheckbox-checkbox': { width: '32px', height: '32px' },
              '& .MuiCheckbox-label': { fontSize: '18px' }
            } : {}}
          />
        );

      case 'radio':
        return (
          <RadioGroup
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            size={touchOptimized ? "lg" : "sm"}
            sx={touchOptimized ? { '& .MuiRadio-label': { fontSize: '18px' } } : {}}
          >
            {field.options?.map((option, index) => (
              <Radio key={index} value={option} label={option} />
            )) || (
              <Radio value="yes" label="Yes" />
            )}
          </RadioGroup>
        );

      case 'dropdown':
        return (
          <Select
            slotProps={{ listbox: { 'data-joy-color-scheme': 'light' } }}
            value={localValue}
            onChange={(_, value) => setLocalValue(value)}
            placeholder={field.label || 'Select option'}
            size={touchOptimized ? "lg" : "sm"}
            sx={touchOptimized ? { fontSize: '18px', minHeight: '56px' } : {}}
          >
            {field.options?.map((option, index) => (
              <Option key={index} value={option}>
                {option}
              </Option>
            )) || (
              <Option value="">No options available</Option>
            )}
          </Select>
        );

      case 'signature':
      case 'initial':
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {localValue ? (
              <Box>
                <img
                  src={localValue}
                  alt={field.type}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '4px',
                    backgroundColor: 'white'
                  }}
                />
                <Typography level="body-xs" sx={{ mt: 1 }}>
                  Click "Save" to confirm
                </Typography>
              </Box>
            ) : (
              <Typography level="body-sm" textColor="text.secondary">
                Click to add {field.type}
              </Typography>
            )}
          </Box>
        );

      default:
        return (
          <Input
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            placeholder={field.label || 'Enter value'}
            size={touchOptimized ? "lg" : "sm"}
            autoFocus
            sx={touchOptimized ? { fontSize: '18px', minHeight: '56px' } : {}}
          />
        );
    }
  };

  const isSignatureField = field.type === 'signature' || field.type === 'initial';

  return (
    <Card
      variant="outlined"
      sx={{
        minWidth: touchOptimized ? 350 : 250,
        maxWidth: touchOptimized ? 600 : 400,
        boxShadow: 'lg'
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography level="title-sm" sx={{ mb: 1 }}>
              {field.label || field.type.charAt(0).toUpperCase() + field.type.slice(1)}
            </Typography>
            {field.required && (
              <Typography level="body-xs" textColor="danger.500">
                * Required field
              </Typography>
            )}
          </Box>

          {isSignatureField ? (
            <Alert color="neutral" size="sm">
              <Typography level="body-xs">
                This {field.type} will be captured in the dedicated signing step.
              </Typography>
            </Alert>
          ) : (
            renderFieldInput()
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size={touchOptimized ? "lg" : "sm"}
              variant="outlined"
              onClick={handleCancel}
              sx={touchOptimized ? { minHeight: '48px', minWidth: '100px' } : {}}
            >
              Cancel
            </Button>
            {!isSignatureField && (
              <Button
                size={touchOptimized ? "lg" : "sm"}
                variant="solid"
                onClick={handleSave}
                sx={touchOptimized ? { minHeight: '48px', minWidth: '100px' } : {}}
              >
                Save
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const SigningFieldRenderer: React.FC<SigningFieldRendererProps> = ({
  fields,
  fieldValues,
  currentPage,
  scale,
  onFieldValueChange,
  onFieldChange,
  recipientRole,
  touchOptimized = false,
  onSignatureFieldClick
}) => {
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);

  // Filter fields for current page and recipient
  const pageFields = currentPage
    ? fields.filter(field =>
        field.page === currentPage &&
        (recipientRole ? (field.signer === recipientRole || field.signer === 'customer') : true)
      )
    : fields;

  const handleFieldClick = useCallback((fieldId: string) => {
    const field = fields.find(f => f.id === fieldId);
    // Don't allow interaction with read-only fields
    if ((field as any)?.readOnly) {
      return;
    }
    if (field && (field.type === 'signature' || field.type === 'initial') && onSignatureFieldClick) {
      onSignatureFieldClick(fieldId);
    } else {
      setActiveFieldId(fieldId);
    }
  }, [fields, onSignatureFieldClick]);

  const handleFieldClose = useCallback(() => {
    setActiveFieldId(null);
  }, []);

  const getFieldIcon = (type: string) => {
    switch (type) {
      case 'signature': return <PenTool size={12} />;
      case 'initial': return <Type size={12} />;
      case 'text': return <Type size={12} />;
      case 'date': return <Calendar size={12} />;
      case 'checkbox': return <CheckSquare size={12} />;
      case 'radio': return <CircleDot size={12} />;
      case 'dropdown': return <ChevronDown size={12} />;
      default: return <Type size={12} />;
    }
  };

  const getFieldBackground = (field: ContractField) => {
    const hasValue = fieldValues[field.id] !== undefined &&
                    fieldValues[field.id] !== '' &&
                    fieldValues[field.id] !== null;
    const isReadOnly = (field as any).readOnly;

    if (isReadOnly && hasValue) {
      return 'rgba(158, 158, 158, 0.1)'; // Light gray for read-only with value
    } else if (hasValue) {
      return 'rgba(46, 125, 50, 0.2)'; // Green for completed
    } else if (field.required) {
      return 'rgba(237, 108, 2, 0.2)'; // Orange for required
    } else {
      return 'rgba(117, 117, 117, 0.1)'; // Gray for optional
    }
  };

  const getBorderColor = (field: ContractField) => {
    const hasValue = fieldValues[field.id] !== undefined &&
                    fieldValues[field.id] !== '' &&
                    fieldValues[field.id] !== null;
    const isReadOnly = (field as any).readOnly;

    if (isReadOnly) {
      return '#9e9e9e'; // Gray for read-only
    } else if (activeFieldId === field.id) {
      return '#1976d2'; // Blue for active
    } else if (hasValue) {
      return '#2e7d32'; // Green for completed
    } else if (field.required) {
      return '#ed6c02'; // Orange for required
    } else {
      return '#757575'; // Gray for optional
    }
  };

  const getFieldDisplayValue = (field: ContractField) => {
    const value = fieldValues[field.id];

    if (value === undefined || value === '' || value === null) {
      return field.label || `Click to fill ${field.type}`;
    }

    switch (field.type) {
      case 'checkbox':
        return value ? '☑' : '☐';
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'signature':
        return value ? '✓ Signed' : 'Click to sign';
      case 'initial':
        return value ? '✓ Initialed' : 'Click to initial';
      default:
        return String(value).substring(0, 20) + (String(value).length > 20 ? '...' : '');
    }
  };

  const activeField = activeFieldId ? fields.find(f => f.id === activeFieldId) : null;

  return (
    <>
      {/* Field Overlays */}
      {pageFields.map((field) => (
        <Box
          key={field.id}
          sx={{
            position: 'absolute',
            left: field.x * scale,
            top: field.y * scale,
            width: field.width * scale,
            height: field.height * scale,
            // Use border-box to include border and padding in the dimensions
            boxSizing: 'border-box',
            backgroundColor: getFieldBackground(field),
            border: `2px solid ${getBorderColor(field)}`,
            borderRadius: '4px',
            cursor: (field as any).readOnly ? 'default' : 'pointer',
            opacity: (field as any).readOnly ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: touchOptimized ? `${Math.max(14, 16 * scale)}px` : `${Math.max(10, 12 * scale)}px`,
            // Minimal padding to not affect dimensions
            padding: `${1 * scale}px`,
            userSelect: 'none',
            transition: 'all 0.2s ease',
            // Remove minHeight to preserve exact dimensions
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              borderColor: '#1976d2',
              transform: 'scale(1.02)'
            }
          }}
          onClick={() => handleFieldClick(field.id)}
        >
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            {getFieldIcon(field.type)}
            <Typography
              level="body-xs"
              sx={{
                fontSize: `${Math.max(9, 10 * scale)}px`,
                fontWeight: 'lg',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                flex: 1
              }}
            >
              {getFieldDisplayValue(field)}
            </Typography>
          </Stack>

          {/* Field status indicator */}
          {fieldValues[field.id] !== undefined &&
           fieldValues[field.id] !== '' &&
           fieldValues[field.id] !== null && (
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                width: 16,
                height: 16,
                backgroundColor: 'success.500',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Check size={8} color="white" />
            </Box>
          )}

          {/* Required indicator */}
          {field.required && (
            fieldValues[field.id] === undefined ||
            fieldValues[field.id] === '' ||
            fieldValues[field.id] === null
          ) && (
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                left: -4,
                width: 8,
                height: 8,
                backgroundColor: 'danger.500',
                borderRadius: '50%'
              }}
            />
          )}
        </Box>
      ))}

      {/* Interactive Field Modal */}
      {activeField && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={handleFieldClose}
        >
          <Box onClick={(e) => e.stopPropagation()}>
            <InteractiveField
              field={activeField}
              value={fieldValues[activeField.id]}
              scale={scale}
              onChange={(value) => {
                if (onFieldChange && typeof activeField.id === 'number') {
                  onFieldChange(activeField.id, value);
                } else if (onFieldValueChange) {
                  onFieldValueChange(activeField.id, value);
                }
              }}
              onClose={handleFieldClose}
              touchOptimized={touchOptimized}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default SigningFieldRenderer;
